import { FirebaseApp, initializeApp }  from 'firebase/app';
import { Firestore, getFirestore, collection, getDoc, getDocs, setDoc, updateDoc, doc, deleteDoc, increment, QuerySnapshot } from 'firebase/firestore';
import { Auth, getAuth, GoogleAuthProvider, signInWithPopup, User, setPersistence, browserSessionPersistence } from "firebase/auth";
import { FirebaseStorage, deleteObject, getStorage, ref, uploadBytes } from "firebase/storage";

export enum OperationResult {
	Success = 'SUCCESS',
	Failure = 'FAILURE'
}

export enum AuthResult {
	Success = 'SUCCESS',
	Error = 'ERROR',
	NotInitialized = 'NOT_INITIALIZED'
}

export interface AuthResponse {
	result: AuthResult;
	errorMessage?: string;
}

export interface FetchResponse {
	success: boolean;
	data?: string;
	error?: string;
}

const FIREBASE_CONFIG = {
	apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
	authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
	databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
	projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
	storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
	appId: process.env.REACT_APP_FIREBASE_APP_ID,
	measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

export default class FBInterface{
	
	private myReq:XMLHttpRequest=new XMLHttpRequest();
	private fbApp:FirebaseApp=initializeApp(FIREBASE_CONFIG);
	private firestoreDb:Firestore=getFirestore(this.fbApp);
	private firebaseStorage:FirebaseStorage=getStorage(this.fbApp);
	private auth:Auth=getAuth(this.fbApp);

	setInterfacePersistence(){
		setPersistence(this.auth, browserSessionPersistence);
	}

	addAuthListener(callback:(user: User | null) => void){
		if(this.auth!==null){
			this.auth.onAuthStateChanged(function(user:User|null) {
				callback(user);
			});
		}
	}

	async signIn(): Promise<AuthResponse> {
		var provider = new GoogleAuthProvider();
		if(this.auth === null){
			return {result: AuthResult.NotInitialized, errorMessage: 'Auth not initialized'};
		}
		try {
			await signInWithPopup(this.auth, provider);
			return {result: AuthResult.Success};
		} catch(error: any) {
			return {result: AuthResult.Error, errorMessage: error.message};
		}
	}

	async signOut(): Promise<AuthResponse> {
		if(this.auth === null){
			return {result: AuthResult.NotInitialized, errorMessage: 'Auth not initialized'};
		}
		try {
			await this.auth.signOut();
			return {result: AuthResult.Success};
		} catch(error: any) {
			return {result: AuthResult.Error, errorMessage: error.message};
		}
	}

	async getOneDoc<T = unknown>(docPath:string): Promise<T> {
		try {
			const myDoc = await getDoc(doc(this.firestoreDb, docPath));
			if(myDoc.exists()){
				return myDoc.data() as T;
			}else {
				return {} as T;
			}
		} catch(err) {
			throw new Error('Failed to get document');
		}
	}

	async getAllDocs(docsPath:string): Promise<QuerySnapshot> {
		try {
			const querySnapshot = await getDocs(collection(this.firestoreDb, docsPath));
			return querySnapshot;
		} catch(err) {
			throw new Error('Failed to get documents');
		}
	}

	async deleteDocuments(deleteList:string[]): Promise<boolean> {
		if(deleteList.length===0) return true;
		try {
			await Promise.all(deleteList.map(myDoc => this.deleteDocument(myDoc)));
			return true;
		} catch(err) {
			return false;
		}
	}

	async deleteDocument(docPath:string): Promise<boolean> {
		try {
			await deleteDoc(doc(this.firestoreDb, docPath));
			return true;
		} catch(err) {
			return false;
		}
	}

	async setDocument(docPath:string, createData:{}): Promise<OperationResult> {
		try {
			await setDoc(doc(this.firestoreDb, docPath), createData);
			return OperationResult.Success;
		} catch(err) {
			return OperationResult.Failure;
		}
	}

	async getJson(jsonPath:string): Promise<FetchResponse> {
		return new Promise((resolve) => {
			const req = new XMLHttpRequest();
			req.onreadystatechange = function(){
				if(this.readyState === 4){
					if (this.status === 200) {
						resolve({success: true, data: this.responseText});
					} else {
						resolve({success: false, error: `HTTP ${this.status}`});
					}
				}
			};
			req.open("GET", jsonPath, true);
			req.send();
		});
	}

	async uploadJson(jsonPath:string, jsonBlob:Blob): Promise<OperationResult> {
		try {
			await uploadBytes(ref(this.firebaseStorage, jsonPath), jsonBlob);
			return OperationResult.Success;
		} catch(err) {
			console.log(err);
			return OperationResult.Failure;
		}
	}

	async deleteJson(jsonPath:string): Promise<OperationResult> {
		try {
			await deleteObject(ref(this.firebaseStorage, jsonPath));
			return OperationResult.Success;
		} catch(err) {
			console.log(err);
			return OperationResult.Failure;
		}
	}

	async incrementCounter(docPath:string): Promise<OperationResult> {
		try {
			await updateDoc(doc(this.firestoreDb, docPath), {
				id: increment(1)
			});
			return OperationResult.Success;
		} catch(err) {
			return OperationResult.Failure;
		}
	}
}
